import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { normalizeReminderNotesForSubmit } from "src/app/cbta-web/ets1/reminder-notes/ets1-reminder-notes-form.utils";
import { isEts1SessionV2 } from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import {
	FormParams,
	PaginationDetails,
	TrainingForms,
	TS1FormRequestJSON,
	TS1FormResponseJSON,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { getFormTypeShortLabel } from "src/app/shared/utils/form-type-label";
import { parseAocIdsFromQuery } from "src/app/util/aoc-filter-options.utils";
import { isEts35FormType } from "./ts1-v2/ts1-form.factories";
@Component({
	selector: "app-forms",
	templateUrl: "./forms.component.html",
	styleUrls: ["./forms.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FormsComponent implements OnInit, OnDestroy, AfterViewInit {
	public columns!: Columns[];
	public configuration!: Config;
	isOnline: boolean = true;

	@ViewChild("trainingNameTpl", { static: true })
	trainingNameTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("sessionTpl", { static: true }) sessionTpl!: TemplateRef<any>;
	@ViewChild("aocTpl", { static: true }) aocTpl!: TemplateRef<any>;
	@ViewChild("statusTpl", { static: true }) statusTpl!: TemplateRef<any>;
	// @ViewChild('cbTpl', {static: true}) cbTpl!: TemplateRef<any>;

	filters?: FormParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	formsSub!: Subscription;
	forms: TrainingForms[] = [];
	routeSub!: Subscription;

	startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1);

	endDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

	dateFilterEnabled = false;

	formTypes: string[] = [];

	selectedAocIds: string[] = [];
	searchQuery = "";
	private searchTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private sharedService: SharedService,
		private router: Router,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private location: Location,
		private cdr: ChangeDetectorRef,
		private roleService: RoleService,
		private dialog: MatDialog,
	) {
		const params = this.route.snapshot.queryParams;
		if (params["startDate"] && params["endDate"]) {
			this.dateFilterEnabled = true;
			this.startDate = moment(params["startDate"]).toDate();
			this.endDate = moment(params["endDate"]).toDate();
		}
	}

	remove(formId: string) {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "remove-form-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Remove",
				modalContent: "Do you want to remove this form?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const dialogSub = dialogRef.afterClosed().subscribe((v) => {
			if (v.close === true) {
				this.loaderService.setLoading(true);
				const sub = this.httpService.formRemove(formId).subscribe(() => {
					this.initFormList();
					sub.unsubscribe();
					this.loaderService.setLoading(false);
				});
			}
			dialogSub.unsubscribe();
		});
	}

	publish(formId: string) {
		this.loaderService.setLoading(true);
		const sub = this.httpService.formPublish(formId).subscribe(() => {
			this.initFormList();
			sub.unsubscribe();
			this.loaderService.setLoading(false);
		});
	}

	unPublish(formId: string) {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "unpublish-form-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Unpublish",
				modalContent: "Do you want to unpublish this form?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const dialogSub = dialogRef.afterClosed().subscribe((v) => {
			if (v.close === true) {
				this.loaderService.setLoading(true);
				const sub = this.httpService.formUnPublish(formId).subscribe(() => {
					this.initFormList();
					sub.unsubscribe();
					this.loaderService.setLoading(false);
				});
			}
			dialogSub.unsubscribe();
		});
	}

	duplicate(formId: string) {
		this.loaderService.setLoading(true);
		const getFormSub = this.httpService.getForm(formId).subscribe((resp: { data: TS1FormResponseJSON }) => {
			const { data } = resp;
			const sessionRemarksByCode = Object.fromEntries(
				(data.sessionDetails ?? [])
					.map((s) => [s.code, s.selectionRemarks?.trim() ?? ""])
					.filter(([, remark]) => remark),
			);
			const sessionRemarksPayload = Object.keys(sessionRemarksByCode).length > 0 ? { sessionRemarksByCode } : {};

			const body: TS1FormRequestJSON =
				data.configVersion === 2
					? {
							name: data.name + " Copy",
							startDate: moment(data.startDate).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
							endDate: moment(data.endDate).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
							aocCode: this.roleService.getUserAOC(),
							formType: data.formType.code,
							configVersion: 2,
							sessionDetails: (data.sessionDetails ?? []).map((s) => s.code),
							...sessionRemarksPayload,
							...(isEts1SessionV2(data)
								? {
										ets1SessionVersion: 2,
										reminderNotes: normalizeReminderNotesForSubmit(data.reminderNotes ?? []),
									}
								: data.reminderNotesEnabled
									? {
											reminderNotesEnabled: true,
											reminderNotesTemplate: data.reminderNotesTemplate ?? "",
										}
									: {}),
							...(data.remoteSignoffEnabled ? { remoteSignoffEnabled: true } : {}),
							...(isEts35FormType(data.formType.code) ? { ets1SessionVersion: data.ets1SessionVersion ?? 1 } : {}),
							assessmentConfig: data.assessmentConfig
								.map((a) => ({
									maneuverType: a.maneuverType.code,
									observations: a.observations.map((o) => ({
										observationType: o.code,
										attempt: o.attempt,
										candidateRole: o.candidateRole,
									})),
								}))
								.filter((c) => c.observations.length > 0),
						}
					: {
							name: data.name + " Copy",
							startDate: moment(data.startDate).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
							endDate: moment(data.endDate).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
							aocCode: this.roleService.getUserAOC(),
							formType: data.formType.code,
							...(data.remoteSignoffEnabled ? { remoteSignoffEnabled: true } : {}),
							assessmentConfig: data.assessmentConfig
								.map((a) => ({
									maneuverType: a.maneuverType.code,
									observations: a.observations.map((o) => ({
										observationType: o.code,
										attempt: o.attempt,
									})),
								}))
								.filter((c) => c.observations.length > 0),
							sessionDetails: data.sessionDetails.map((a) => a.code),
							...sessionRemarksPayload,
							...(isEts35FormType(data.formType.code) ? { ets1SessionVersion: data.ets1SessionVersion ?? 1 } : {}),
						};

			const duplicateFormSub = this.httpService.addForm(body).subscribe(() => {
				this.initFormList();
				this.loaderService.setLoading(false);
				duplicateFormSub.unsubscribe();
			});

			getFormSub.unsubscribe();
		});
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FORMS);
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
			this.cdr.detectChanges();
		});

		this.initTableConfig();
	}

	ngAfterViewInit(): void {
		this.initParams();
	}

	ngOnDestroy(): void {
		if (this.formsSub) {
			this.formsSub.unsubscribe();
		}
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	}

	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			...(this.isGroupAdmin
				? [
						{
							key: "aoc",
							title: "AOC",
							cellTemplate: this.aocTpl,
							width: "12%",
						},
					]
				: []),
			{
				key: "formTypeLabel",
				title: "Form Type",
				cellTemplate: this.formTypeTpl,
				width: "22%",
			},
			{
				key: "name",
				title: "Test Name",
				cellTemplate: this.trainingNameTpl,
				width: "28%",
			},
			{
				key: "updatedDate",
				title: "Submission Date/Time",
				cellTemplate: this.dateTpl,
				width: "22%",
			},
			{
				key: "",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "16%",
			},
		];
	}

	private buildListParams(): FormParams {
		const params: FormParams = {
			...this.filters,
			role: this.roleService.getUserRole(),
		};

		if (this.isGroupAdmin) {
			if (this.selectedAocIds.length) {
				params.aocIds = this.selectedAocIds;
			} else {
				delete params.aocIds;
			}
		} else {
			delete params.aocIds;
		}

		return params;
	}

	initParams(): void {
		setTimeout(() => {
			this.routeSub = this.route.queryParams.subscribe((params) => {
				const hasDates = Boolean(params["startDate"] && params["endDate"]);
				this.dateFilterEnabled = hasDates;
				this.updateFilters({
					...this.filters,
					...params,
					...(!hasDates
						? {
								startDate: undefined,
								endDate: undefined,
							}
						: {}),
				});
			});
		});
	}

	initFormList(): void {
		this.loaderService.setLoading(true);
		this.formsSub = this.httpService.forms(this.buildListParams()).subscribe((result) => {
			const data = result.data;
			const forms = data.data as TrainingForms[];

			const total: number = data.total;

			this.forms = forms.map((form) => ({
				...form,
				formTypeLabel: this.formTypeLabel(form.formType ?? form.formTypeCode),
			}));

			const paginationDetails = this.calculatePages(total, this.size);

			this.pagination = {
				limit: this.size,
				offset: this.page,
				count: this.size * paginationDetails.totalPages,
			};

			this.cdr.detectChanges();

			this.loaderService.setLoading(false);
		});
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;

				this.updateFilters({
					...this.filters,
					page: value.page,
					size: value.limit,
					// aocCode: this.roleService.getUserAOC(),
					role: this.roleService.getUserRole(),
				});

				break;

			// other cases

			default:
				// do nothing
				break;
		}
	}

	edit(rowIndex: number): void {
		this.router.navigate([RouteType.FORMS, rowIndex]);
	}

	viewForm(formId: string): void {
		this.router.navigate([RouteType.FORMS, formId], {
			queryParams: this.filters,
		});
	}

	onFormTypeChanged(value: string[] = []) {
		this.updateFilters({
			...this.filters,
			formTypes: value,
			page: 1,
			role: this.roleService.getUserRole(),
		});
	}

	onAocFilterChanged(aocIds: string[]): void {
		this.selectedAocIds = aocIds ?? [];

		this.updateFilters({
			...this.filters,
			aocIds: this.selectedAocIds.length ? this.selectedAocIds : undefined,
			page: 1,
			role: this.roleService.getUserRole(),
		});
	}

	onSearch(): void {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		this.searchTimeout = setTimeout(() => {
			this.updateFilters({
				...this.filters,
				search: this.searchQuery.trim(),
				page: 1,
				role: this.roleService.getUserRole(),
			});
		}, 300);
	}

	handleDateRangeChange(dateRange: DateRange) {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
			this.updateFilters({
				...this.filters,
				startDate: undefined,
				endDate: undefined,
				page: 1,
				role: this.roleService.getUserRole(),
			});
			return;
		}

		if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
			this.updateFilters({
				...this.filters,
				startDate: moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				endDate: moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				page: 1,
				role: this.roleService.getUserRole(),
			});
		}
	}

	updateFilters(filters: FormParams) {
		if (this.isGroupAdmin) {
			this.selectedAocIds = filters.aocIds?.length ? filters.aocIds : parseAocIdsFromQuery(filters.aocId);
		}

		const incomingFormTypes = filters?.formTypes;
		const nextFormTypes = this.isGroupAdmin
			? []
			: Array.isArray(incomingFormTypes)
				? incomingFormTypes.map(String)
				: incomingFormTypes
					? [String(incomingFormTypes)]
					: this.formTypes;
		const searchText = Object.prototype.hasOwnProperty.call(filters, "search")
			? String(filters.search ?? "").trim()
			: (this.filters?.search ?? "");

		this.formTypes = nextFormTypes;
		if (Object.prototype.hasOwnProperty.call(filters, "search")) {
			this.searchQuery = String(filters.search ?? "");
		}
		this.filters = {
			page: filters.page ?? this.constant.PAGINATION.DEFAULT_PAGE,
			size: filters.size ?? this.constant.PAGINATION.DEFAULT_SIZE,
			role: filters.role ?? this.roleService.getUserRole(),
			...(filters.startDate ? { startDate: filters.startDate } : {}),
			...(filters.endDate ? { endDate: filters.endDate } : {}),
			...(nextFormTypes.length ? { formTypes: nextFormTypes } : {}),
			...(this.isGroupAdmin && this.selectedAocIds.length ? { aocIds: this.selectedAocIds } : {}),
			...(searchText ? { search: searchText } : {}),
		};

		if (!isEmpty(filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}

		if (!isEmpty(filters?.startDate)) {
			this.startDate = moment(filters.startDate).toDate();
		}

		if (!isEmpty(filters?.endDate)) {
			this.endDate = moment(filters.endDate).toDate();
		}

		this.updateUrl();

		this.initFormList();
	}

	updateUrl(): void {
		const queryParams: FormParams = {
			page: this.page,
			size: this.size,
			role: this.roleService.getUserRole(),
		};

		if (this.filters?.startDate) {
			queryParams.startDate = this.filters.startDate;
		}
		if (this.filters?.endDate) {
			queryParams.endDate = this.filters.endDate;
		}
		if (this.formTypes.length) {
			queryParams.formTypes = this.formTypes;
		}
		if (this.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		if (this.filters?.search) {
			queryParams.search = this.filters.search;
		}

		const url = this.router.serializeUrl(
			this.router.createUrlTree([RouteType.FORMS], {
				queryParams,
			}),
		);

		this.location.go(url);
	}

	formatDate(dateString: string): string {
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	createNewForm(): void {
		this.router.navigate([RouteType.NEW_FORM]);
	}

	isEditable(form: TrainingForms): boolean {
		return form.aoc?.toUpperCase() === this.roleService.getUserAOC().toUpperCase() || !this.isOnline;
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;

		return { totalPages, lastPageSize };
	}

	getSessionConfig(sessionConfig: string[]) {
		return sessionConfig.join(", ");
	}

	formTypeLabel(formType?: { code?: string } | string): string {
		const code = typeof formType === "string" ? formType : formType?.code;
		return getFormTypeShortLabel(code) || "-";
	}

	get filterOptions() {
		return this.sharedService.filterOptions;
	}

	get aocFilterOptions() {
		return this.sharedService.aocFilterOptions;
	}

	get isGroupAdmin(): boolean {
		return this.roleService.isGroupAdmin;
	}
}
